import { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from "recharts";
import { CircleNotch, ChartBar, TrendUp } from "@phosphor-icons/react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
const fmtMonth = (m) => {
  if (!m) return m;
  const [y, mo] = m.split("-");
  const d = new Date(Number(y), Number(mo) - 1, 1);
  return d.toLocaleDateString("en-AU", { month: "short", year: "2-digit" });
};

function Panel({ title, icon: Icon, children, testid }) {
  return (
    <div className="rounded-sm border border-neutral-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-black tracking-tight text-[#0A0A0A]">
        <Icon size={18} weight="bold" className="text-[#0055FF]" /> {title}
      </h3>
      {children}
    </div>
  );
}

const tooltipStyle = { fontSize: 12, borderRadius: 2, border: "1px solid #e5e5e5" };

export default function TimesheetCharts({ filters }) {
  const [data, setData] = useState(undefined);

  useEffect(() => {
    setData(undefined);
    api.get("/timesheets/summary", { params: filters })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [filters.technician, filters.client, filters.worktype, filters.start_date, filters.end_date]);

  if (data === undefined)
    return <div className="mt-6 flex h-48 items-center justify-center rounded-sm border border-neutral-200 bg-white" data-testid="ts-charts-loading"><CircleNotch size={26} className="animate-spin text-[#0055FF]" /></div>;
  if (data === null) return null;

  const months = data.by_month || [];
  const clients = data.top_clients || [];
  const techs = data.top_technicians || [];

  return (
    <div className="mt-6 space-y-4" data-testid="ts-charts">
      <Panel title="Labour by month — hours & charge" icon={TrendUp} testid="ts-chart-monthly">
        {months.length === 0 ? (
          <p className="py-8 text-center text-sm text-neutral-400">No data for these filters.</p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={months} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11 }} />
              <YAxis yAxisId="h" tick={{ fontSize: 11 }} width={45} />
              <YAxis yAxisId="c" orientation="right" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} width={45} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => (n === "charge" ? money(v) : `${v} hrs`)} labelFormatter={fmtMonth} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="h" type="monotone" dataKey="hours" name="Hours" stroke="#0055FF" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line yAxisId="c" type="monotone" dataKey="charge" name="Charge" stroke="#059669" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Panel>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Top clients by charge" icon={ChartBar} testid="ts-chart-clients">
          {clients.length === 0 ? (
            <p className="py-8 text-center text-sm text-neutral-400">No client data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, clients.length * 30)}>
              <BarChart data={clients} layout="vertical" margin={{ top: 0, right: 16, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="client" width={130} tick={{ fontSize: 11 }} interval={0}
                  tickFormatter={(v) => (v.length > 20 ? v.slice(0, 19) + "…" : v)} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
                <Bar dataKey="charge" name="Charge" fill="#0055FF" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Top technicians by hours" icon={ChartBar} testid="ts-chart-techs">
          {techs.length === 0 ? (
            <p className="py-8 text-center text-sm text-neutral-400">No technician data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(220, techs.length * 30)}>
              <BarChart data={techs} layout="vertical" margin={{ top: 0, right: 16, left: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="technician" width={130} tick={{ fontSize: 11 }} interval={0}
                  tickFormatter={(v) => (v.length > 20 ? v.slice(0, 19) + "…" : v)} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v} hrs`} />
                <Bar dataKey="hours" name="Hours" fill="#7C3AED" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>
    </div>
  );
}
